/**
 * On-Score Tip Coordinate System
 * 
 * This module manages the conversion between physical screen coordinates
 * and musical anchors (measure/beat positions) for placing tips on the score.
 * 
 * The key challenge is that coordinates change when parts are shown/hidden,
 * so we use musical anchors that remain stable.
 */

export class OnScoreTipCoordinates {
  constructor(osmdInstance) {
    this.osmd = osmdInstance;
    this.measureCache = new Map();
  }

  /**
   * Convert screen coordinates to musical anchor
   * @param {number} x - Screen X coordinate
   * @param {number} y - Screen Y coordinate
   * @param {Array} visibleParts - Currently visible parts
   * @returns {Object} Musical anchor: { measureNumber, beat, staffIndex, part }
   */
  screenToMusicalAnchor(x, y, visibleParts = []) {
    // console.log('screenToMusicalAnchor called with:', { x, y, hasOsmd: !!this.osmd, hasCursor: !!this.osmd?.cursor });
    
    if (!this.osmd?.cursor) {
      // console.log('No OSMD or cursor available');
      return null;
    }

    try {
      // Find the closest measure using OSMD's built-in functionality
      const measures = this.getAllMeasureElements();
      // console.log('Found measures:', measures.length);
      
      if (measures.length === 0) {
        // console.log('No measures found in score');
        return null;
      }
      
      let closestMeasure = null;
      let minDistance = Infinity;
      
      for (const measureElement of measures) {
        const bbox = measureElement.getBoundingClientRect();
        const centerX = bbox.left + bbox.width / 2;
        const centerY = bbox.top + bbox.height / 2;
        
        const distance = Math.sqrt(
          Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
        );
        
        // Removed console spam
        
        if (distance < minDistance) {
          minDistance = distance;
          closestMeasure = measureElement;
        }
      }

      if (!closestMeasure) {
        // console.log('No closest measure found');
        return null;
      }

      // console.log('Closest measure found, extracting info...');
      // Extract measure information from the DOM element
      const measureInfo = this.extractMeasureInfo(closestMeasure, x, y);
      
      if (measureInfo) {
        // Add visible parts context
        measureInfo.visibleParts = [...visibleParts];
        measureInfo.timestamp = Date.now();
        // console.log('Created measure info:', measureInfo);
      } else {
        // console.log('Failed to extract measure info');
      }

      return measureInfo;
    } catch (error) {
      console.error('Error converting screen to musical anchor:', error);
      return null;
    }
  }

  /**
   * Convert musical anchor back to screen coordinates
   * @param {Object} anchor - Musical anchor
   * @param {Array} currentVisibleParts - Currently visible parts
   * @returns {Object} Screen coordinates: { x, y, valid }
   */
  musicalAnchorToScreen(anchor, currentVisibleParts = []) {
    if (!anchor || !this.osmd?.cursor) {
      return { x: 0, y: 0, valid: false };
    }

    try {
      // Find the measure element for this anchor
      const measureElement = this.findMeasureElement(anchor.measureNumber);
      
      if (!measureElement) {
        return { x: 0, y: 0, valid: false };
      }

      const bbox = measureElement.getBoundingClientRect();
      
      // Calculate approximate position within the measure
      let x = bbox.left + (bbox.width * (anchor.beat || 0.5)); // Default to middle of measure
      let y = bbox.top + (bbox.height * 0.5); // Middle of staff
      
      // Adjust for staff if specified
      if (anchor.staffIndex !== undefined) {
        const staffHeight = bbox.height / this.getStaffCountForMeasure(anchor.measureNumber);
        y = bbox.top + (staffHeight * anchor.staffIndex) + (staffHeight * 0.5);
      }

      return { 
        x: Math.round(x), 
        y: Math.round(y), 
        valid: true,
        measureElement: measureElement
      };
    } catch (error) {
      console.error('Error converting musical anchor to screen:', error);
      return { x: 0, y: 0, valid: false };
    }
  }

  /**
   * Get all measure elements from the rendered score
   */
  getAllMeasureElements() {
    if (!this.osmd?.container) {
      // console.log('No OSMD container available');
      return [];
    }

    // Look for measure elements in the SVG
    const measures = this.osmd.container.querySelectorAll('.measure, [data-measure], .vf-measure');
    // console.log('getAllMeasureElements: container:', this.osmd.container);
    // console.log('getAllMeasureElements: found', measures.length, 'measures with selectors: .measure, [data-measure], .vf-measure');
    
    // If no measures found with common selectors, try broader search
    if (measures.length === 0) {
      const allSvgElements = this.osmd.container.querySelectorAll('svg *');
      // console.log('No measures found, total SVG elements:', allSvgElements.length);
      
      // Try to find any g elements that might be measures
      const gElements = this.osmd.container.querySelectorAll('g');
      // console.log('Found g elements:', gElements.length);
      
      // Log some g elements to see their structure
      for (let i = 0; i < Math.min(5, gElements.length); i++) {
        const g = gElements[i];
        // console.log(`G element ${i}:`, {
        //   className: g.className,
        //   classList: Array.from(g.classList || []),
        //   attributes: Array.from(g.attributes || []).map(attr => `${attr.name}="${attr.value}"`),
        //   children: g.children.length
        // });
      }
    }
    
    return Array.from(measures);
  }

  /**
   * Extract measure information from a DOM element and click position
   */
  extractMeasureInfo(measureElement, clickX, clickY) {
    try {
      // Try to get measure number from various possible attributes
      let measureNumber = measureElement.getAttribute('data-measure') ||
                         measureElement.getAttribute('data-measure-number') ||
                         measureElement.getAttribute('measure') ||
                         this.extractMeasureNumberFromClass(measureElement);

      if (!measureNumber) {
        // Try to find measure number from parent or siblings
        measureNumber = this.findMeasureNumberInHierarchy(measureElement);
      }

      if (!measureNumber) {
        // Fallback: estimate measure number based on position in the measures array
        const allMeasures = this.getAllMeasureElements();
        const measureIndex = Array.from(allMeasures).indexOf(measureElement);
        measureNumber = measureIndex >= 0 ? measureIndex + 1 : 1;
        // console.log(`Using fallback measure number: ${measureNumber} (index: ${measureIndex})`);
      }

      const bbox = measureElement.getBoundingClientRect();
      
      // Calculate relative position within measure (0-1)
      const relativeX = Math.max(0, Math.min(1, (clickX - bbox.left) / bbox.width));
      const relativeY = Math.max(0, Math.min(1, (clickY - bbox.top) / bbox.height));
      
      // console.log(`Debug beat calc: clickX=${clickX}, bbox.left=${bbox.left}, bbox.width=${bbox.width}, relativeX=${relativeX})`);
      
      // Beat position should be 0-1 relative position within measure
      // The display code will multiply by 4 and add 1 to show user-friendly beat numbers
      const estimatedBeat = relativeX; // 0.0 to 1.0 relative position in measure
      
      // Estimate staff index based on Y position
      const staffCount = this.getStaffCountForMeasure(measureNumber);
      const staffIndex = Math.floor(relativeY * staffCount);
      
      // console.log(`Debug staff calc: clickY=${clickY}, bbox.top=${bbox.top}, bbox.height=${bbox.height}, relativeY=${relativeY}, staffCount=${staffCount}, staffIndex=${staffIndex});

      return {
        measureNumber: parseInt(measureNumber),
        beat: estimatedBeat,
        staffIndex: staffIndex,
        relativeX: relativeX,
        relativeY: relativeY,
        part: null // Will be determined by context
      };
    } catch (error) {
      console.error('Error extracting measure info:', error);
      return null;
    }
  }

  /**
   * Find measure element by measure number
   */
  findMeasureElement(measureNumber) {
    const measures = this.getAllMeasureElements();
    
    for (const measure of measures) {
      const elementMeasureNum = measure.getAttribute('data-measure') ||
                               measure.getAttribute('data-measure-number') ||
                               this.extractMeasureNumberFromClass(measure) ||
                               this.findMeasureNumberInHierarchy(measure);
      
      if (parseInt(elementMeasureNum) === measureNumber) {
        return measure;
      }
    }
    
    return null;
  }

  /**
   * Extract measure number from CSS class names
   */
  extractMeasureNumberFromClass(element) {
    const classList = Array.from(element.classList);
    for (const className of classList) {
      const match = className.match(/measure[-_]?(\d+)/i);
      if (match) {
        return match[1];
      }
    }
    return null;
  }

  /**
   * Find measure number by looking at parent/sibling elements
   */
  findMeasureNumberInHierarchy(element) {
    // Check parent elements
    let parent = element.parentElement;
    while (parent) {
      const measureNum = parent.getAttribute('data-measure') ||
                        parent.getAttribute('data-measure-number') ||
                        this.extractMeasureNumberFromClass(parent);
      if (measureNum) {
        return measureNum;
      }
      parent = parent.parentElement;
    }

    // Check siblings
    const siblings = element.parentElement?.children || [];
    for (const sibling of siblings) {
      const measureNum = sibling.getAttribute('data-measure') ||
                        sibling.getAttribute('data-measure-number') ||
                        this.extractMeasureNumberFromClass(sibling);
      if (measureNum) {
        return measureNum;
      }
    }

    return null;
  }

  /**
   * Get the number of staffs for a given measure
   */
  getStaffCountForMeasure(measureNumber) {
    try {
      // Use OSMD's Sheet structure if available
      if (this.osmd?.Sheet?.Instruments) {
        return this.osmd.Sheet.Instruments.length;
      }
      
      // Fallback: count staff elements in the measure
      const measureElement = this.findMeasureElement(measureNumber);
      if (measureElement) {
        const staffElements = measureElement.querySelectorAll('.staff, [data-staff], .vf-staff');
        return Math.max(1, staffElements.length);
      }
      
      return 1; // Default to single staff
    } catch (error) {
      console.error('Error getting staff count:', error);
      return 1;
    }
  }

  /**
   * Check if a musical anchor is still valid with current part visibility
   */
  isAnchorValid(anchor, currentVisibleParts = []) {
    if (!anchor) return false;
    
    try {
      const screenPos = this.musicalAnchorToScreen(anchor, currentVisibleParts);
      return screenPos.valid;
    } catch (error) {
      console.error('Error validating anchor:', error);
      return false;
    }
  }

  /**
   * Update all tip coordinates when part visibility changes
   */
  updateTipCoordinatesForVisibilityChange(tips, newVisibleParts) {
    const updatedTips = [];
    
    for (const tip of tips) {
      if (tip.type === 'on-score' && tip.position) {
        const newScreenPos = this.musicalAnchorToScreen(tip.position, newVisibleParts);
        
        if (newScreenPos.valid) {
          updatedTips.push({
            ...tip,
            screenPosition: newScreenPos,
            visible: true
          });
        } else {
          // Tip's anchor is no longer valid (e.g., part was hidden)
          updatedTips.push({
            ...tip,
            visible: false
          });
        }
      } else {
        // Off-score tips are not affected
        updatedTips.push(tip);
      }
    }
    
    return updatedTips;
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.measureCache.clear();
    this.osmd = null;
  }
}

export default OnScoreTipCoordinates;